import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { microToneFramework } from '@/content/microToneFramework';

export interface MicroAIContent {
  headline: string;
  blurb: string;
  tip: string;
}

const FALLBACK: MicroAIContent = {
  headline: "You're right on time.",
  blurb: "Let’s grab one tiny win together.",
  tip: 'Log the effort, even if it was just a minute.'
};

export const useMicroAIContent = () => {
  const [content, setContent] = useState<MicroAIContent>(FALLBACK);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      const storyArc = microToneFramework.arc
        .map((segment) => `${segment.stage}: ${segment.intent}`)
        .join(' | ');
      const pillars = microToneFramework.pillars
        .map((pillar) => `${pillar.label} (${pillar.description})`)
        .join(' | ');
      const reminders = microToneFramework.reminders.join(' ');

      try {
        const { data, error } = await supabase.functions.invoke('generate-character-content', {
          body: {
            characterType: 'lyra',
            contentType: 'micro_daily',
            topic: 'micro learning daily mix',
            context: [
              'Generate JSON with keys headline, blurb, tip.',
              `Tone mood: ${microToneFramework.mood}`,
              `Story arc: ${storyArc}`,
              `Pillars: ${pillars}`,
              `Reminders: ${reminders}`,
              'Headline <= 48 chars, other fields <= 110 chars.',
              'Each field should read like a warm teammate cheering on a tiny win.'
            ].join(' ')
          }
        });

        if (error) throw error;

        let parsed: Partial<MicroAIContent> | null = null;
        if (data?.content) {
          try {
            parsed = JSON.parse(data.content);
          } catch {
            // fall back to heuristics by splitting lines
            const lines = data.content.split('\n').map((line: string) => line.trim()).filter(Boolean);
            parsed = {
              headline: lines[0],
              blurb: lines[1],
              tip: lines[2]
            };
          }
        }

        if (parsed) {
          setContent({
            headline: parsed.headline || FALLBACK.headline,
            blurb: parsed.blurb || FALLBACK.blurb,
            tip: parsed.tip || FALLBACK.tip
          });
        }
      } catch (err) {
        console.warn('[useMicroAIContent] Falling back to defaults. Confirm generate-character-content edge function allows your dev origin via CORS.', err);
        setContent(FALLBACK);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  return { content, loading };
};
